/**
 * "Ask Cortex how to complete this task" deep link (sender side).
 *
 * Contract v1 is owned by Cortex and documented in
 * perry-health/Orchestration-Manager-Front-End-Spec
 * docs/superpowers/specs/2026-09-02-cortex-ask-deep-link-design.md — Part A is
 * the receiver, Part B is this sender.
 *
 * The message travels in a URL, and URLs land in browser history, Cloudflare
 * Access login logs and Cortex's recents list on shared clinic machines. So
 * everything that reaches it is built from allow-listed values only: literal
 * unions looked up through label tables and never echoed raw, numbers and
 * booleans checked at runtime, dates rendered as relative days, and authored
 * enum constants passed through the authored-text screen below.
 *
 * Two fields are authored rather than typed and so may be quoted, but only for
 * workflow-generated tasks and only after that screen: taskDescription and
 * taskSop, both written in workflow-library YAML. On a delegated sub-task the
 * same two fields hold what the delegating coordinator typed, so they are
 * denied there.
 *
 * Everything else free-text is never read: title (admin-overridable and
 * templated), caption, evidence summary and source, escalation and cancellation
 * notes, conversation text. No Patient field is read at all — including phase,
 * which normalizeDashboardV2Task falls back to patient.phase. The task id
 * travels only in `ref`.
 *
 * The finished message is run through a vendored copy of Cortex's own screens
 * (cortexPhiGuard.js) and the link is withheld on any hit.
 */
import { roles } from 'common-src/models/Client';
import { getTaskReasonById } from 'common-src/models/PatientTask';
import { TaskType } from 'common-src/models/PatientTask/constants';

import {
  ASSESSMENT_TASK_TYPES,
  ASSESSMENT_TASK_VARIANTS,
  BLOCKED_REASON_LABELS,
  BLOCKED_STATUS,
  ESCALATED_STATUS,
  ESCALATED_TO_OPTIONS,
  EVIDENCE_TYPES,
  pathwayLabel,
  TASK_DISPLAY_STATUS,
  TASK_PRIORITY,
  teamMeta,
} from './constants';
import { cortexGuard } from './cortexPhiGuard';
import {
  getTaskAssessmentType,
  getTaskAssessmentVariant,
  getTaskDisplayStatus,
  isAssessmentTask,
  isMessageTask,
  isTaskCaregiverAssisted,
  isTaskReadinessComplete,
} from './helpers';

export const CORTEX_CONTRACT_VERSION = '1';
export const CORTEX_ORIGIN = 'https://cortex.mindspan.co';
export const CORTEX_SOURCE = 'om';
export const CORTEX_Q_MAX = 1500;
export const CORTEX_URL_MAX = 4000;
export const CORTEX_REF_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

export const CORTEX_ASK_LABEL = 'Ask Cortex how to complete this task';

export const CLAIM_STATE = {
  ClaimedByMe: 'claimed-by-me',
  ClaimedByOther: 'claimed-by-other',
  Unclaimed: 'unclaimed',
};

/**
 * Authored text (task reason, evidence, blocked reason and assessment labels)
 * is allow-listed by where it comes from — frozen enum tables in the codebase —
 * rather than by what it says, so it gets one more check: a fragment carrying a
 * calendar date, a year, a URL, an honorific followed by a name, or an "@" is
 * dropped rather than emitted.
 */
const AUTHORED_DENY = [
  /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}\b/i,
  /\b\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i,
  /\b(?:19|20)\d{2}\b/,
  /\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/,
  /\b\d{1,2}[.-]\d{1,2}[.-]\d{2,4}\b/,
  /https?:\/\//i,
  /\b(?:Dr|Mr|Mrs|Ms|Miss)\.?\s+[A-Z]/,
  /@/,
];

/** The authored-text screen: the trimmed text, or null when it must not be emitted. */
export const authored = (text) => {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  return AUTHORED_DENY.some((pattern) => pattern.test(trimmed)) ? null : trimmed;
};

/**
 * Role labels a task may carry, looked up rather than echoed.
 *
 * Orchestration tasks carry the snake_case `owner_role` slugs authored in the
 * workflow-library task catalog and workflow YAML; tasks created in the app
 * carry a Client roster label. Both vocabularies are fixed and authored, so
 * both are mapped here and anything else is dropped. System and Viewer are not
 * assignable work roles.
 */
export const CORTEX_ROLE_LABELS = new Map([
  ['care_coordination', 'Care Coordination'],
  ['caregiver_liaison', 'Caregiver Liaison'],
  ['clinical', 'Clinical'],
  ['member_experience', 'Member Experience'],
  ['provider', 'Provider'],
  ['rcm', 'RCM'],
  ...Object.values(roles)
    .filter((role) => role.id !== roles.System.id && role.id !== roles.Viewer.id)
    .map((role) => [role.label, role.label]),
]);

/**
 * Workflow steps set `evidence_type` to the visit stage they belong to, not to
 * one of the app's EVIDENCE_TYPES keys, so both vocabularies are mapped. The
 * stage form reads as a position in the workflow rather than as a flag.
 */
const WORKFLOW_STAGE_LABELS = {
  'Pre-Visit Preparation': 'pre-visit preparation',
  'Pre-Visit Readiness Gate': 'pre-visit readiness gate',
  'Phase-2 Prerequisite': 'phase 2 prerequisite',
  'Visit Execution': 'visit execution',
  'Post-Visit Extraction': 'post-visit extraction',
};

const PROTOCOL_LABELS = { core: 'core', edge: 'edge' };

const PRIORITY_LABELS = {
  [TASK_PRIORITY.Critical]: 'critical',
  [TASK_PRIORITY.High]: 'high',
  [TASK_PRIORITY.Medium]: 'medium',
  [TASK_PRIORITY.Low]: 'low',
};

const STATUS_LABELS = {
  [TASK_DISPLAY_STATUS.Pending]: 'pending',
  [TASK_DISPLAY_STATUS.InProgress]: 'in progress',
};

const EVIDENCE_LABELS = Object.entries(EVIDENCE_TYPES).reduce(
  (acc, [key, meta]) => ({ ...acc, [key]: meta.label }),
  {},
);

const ASSESSMENT_TYPE_LABELS = Object.values(ASSESSMENT_TASK_TYPES).reduce(
  (acc, meta) => ({ ...acc, [meta.value]: meta.label }),
  {},
);

const ASSESSMENT_VARIANT_LABELS = Object.values(ASSESSMENT_TASK_VARIANTS).reduce(
  (acc, meta) => ({ ...acc, [meta.value]: meta.label }),
  {},
);

const TEAM_LABELS = Object.entries(teamMeta).reduce(
  (acc, [key, meta]) => ({ ...acc, [key]: meta.label }),
  {},
);

const ESCALATION_TEAM_LABELS = ESCALATED_TO_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {},
);

// ─── value helpers ───

const pick = (table, value) => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  return table[value] ?? null;
};

// ─── relative time (never an absolute date) ───

const DAY_MS = 86400000;

const days = (count) => `${count} day${count === 1 ? '' : 's'}`;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/;

/**
 * Whole calendar days between a due date and now, matching daysUntil() in
 * helpers.js so the sentence agrees with the due date the panel shows. Raw
 * timestamp subtraction does not: at 18:00 local, a due date at the next
 * midnight is 0.25 days away and would round to "today" while the panel reads
 * "Tomorrow".
 */
const dayDiff = (isoValue, now) => {
  if (typeof isoValue !== 'string') return null;

  const dateOnlyMatch = isoValue.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const target = new Date(Number(year), Number(month) - 1, Number(day));
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((target - today) / DAY_MS);
  }

  const parsed = Date.parse(isoValue);
  if (Number.isNaN(parsed)) return null;
  return Math.round((parsed - now.getTime()) / DAY_MS);
};

export const relativeDue = (isoValue, now) => {
  const diff = dayDiff(isoValue, now);
  if (diff === null) return null;
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  return diff > 0 ? `in ${days(diff)}` : `${days(-diff)} overdue`;
};

// ─── small text helpers ───

const article = (value) => (/^[aeiou]/i.test(value) ? 'an' : 'a');

const ensurePeriod = (value) => {
  const trimmed = value.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const dropTrailingPeriod = (value) => value.trim().replace(/\.$/, '');

const roleLabel = (role) =>
  typeof role === 'string' ? (CORTEX_ROLE_LABELS.get(role.trim()) ?? null) : null;

/**
 * True for a task the workflow engine authored. Its taskDescription and taskSop
 * come from workflow-library YAML and are safe to quote after the authored-text
 * screen. A delegated sub-task's description is typed by the delegating
 * coordinator ("this becomes the task description" in DelegateSubtasksModal),
 * so it is free text and stays denied.
 */
const isWorkflowAuthored = (task) =>
  !task?.parentTaskId &&
  !!(`${task?.workflowInstanceId ?? ''}`.trim() || `${task?.stateName ?? ''}`.trim());

const reasonLabel = (task) => authored(getTaskReasonById(task?.reasonId)?.label);

// ─── per-type action phrase and sentence, structured fields only ───

const NONE = { phrase: null, sentence: null };

/**
 * The production task model carries structured detail for assessment, member
 * message and pre-visit intake work; everything else is described by its reason
 * alone rather than by guessing at free-text fields.
 */
const typeText = (task) => {
  if (isAssessmentTask(task)) {
    // Looked up, never echoed: getTaskAssessmentTypeLabel falls back to the raw
    // backend value for anything outside the enum, which would put an arbitrary
    // string into the URL.
    const instrument = pick(ASSESSMENT_TYPE_LABELS, getTaskAssessmentType(task));
    if (!instrument) return NONE;
    const variant = pick(ASSESSMENT_VARIANT_LABELS, getTaskAssessmentVariant(task));
    const assisted = isTaskCaregiverAssisted(task) ? ', caregiver-assisted' : '';
    return {
      phrase: `review ${article(instrument)} ${instrument} screening result`,
      sentence: `The instrument is ${instrument}${variant ? `, ${variant} variant` : ''}${assisted}.`,
    };
  }

  if (isMessageTask(task)) {
    return { phrase: 'reply to a member portal message', sentence: null };
  }

  if (task?.typeId === TaskType.PreVisitIntake.value) {
    return { phrase: 'complete a pre-visit intake step', sentence: null };
  }

  return NONE;
};

// ─── headline, provenance, assembly ───

const headline = (task, ctx, phrase) => {
  const reason = task?.parentTaskId ? reasonLabel(ctx?.parentTask) : reasonLabel(task);
  if (!reason) return null;

  const lead = task?.parentTaskId
    ? `Delegated sub-task of ${article(reason)} ${dropTrailingPeriod(reason)} task`
    : dropTrailingPeriod(reason);

  return phrase ? `${lead}: ${phrase}.` : `${lead}.`;
};

const claimSentence = (claimState, team) => {
  if (claimState === CLAIM_STATE.ClaimedByMe) return 'I have claimed this task.';
  if (claimState === CLAIM_STATE.ClaimedByOther) {
    return `This task is assigned to someone else on the ${team} team and I am covering it.`;
  }
  return 'This task is unclaimed and I am deciding whether to pick it up.';
};

const isEscalatedToTeam = (task) =>
  task?.escalatedStatus === ESCALATED_STATUS.Escalated && !`${task?.owner ?? ''}`.trim();

/**
 * Builds the identifier-free situation text, or null when no safe message can
 * be formed for this task.
 */
export const buildCortexAskMessage = (task, ctx = {}) => {
  if (!task) return null;
  const now = ctx.now ?? new Date();

  const type = typeText(task);
  const lead = headline(task, ctx, type.phrase);
  if (!lead) return null;

  // Escalating to a team hands the task to that team; describe it the way the
  // panel does, so the sentence stays truthful about who is holding it.
  const escalated = isEscalatedToTeam(task);
  const escalationTeam = escalated ? pick(ESCALATION_TEAM_LABELS, task.escalatedTo) : null;
  const team = escalationTeam ?? pick(TEAM_LABELS, task.team);
  if (!team) return null;

  const role = roleLabel(task.role);
  const pathway = pick(pathwayLabel, task.pathway);
  const protocol = pick(PROTOCOL_LABELS, task.protocol);
  const evidence = authored(pick(EVIDENCE_LABELS, task?.evidence?.type));
  const stage = pick(WORKFLOW_STAGE_LABELS, task?.evidence?.type);
  // Authored in workflow-library, not typed by anyone: task_sop is a SOP title
  // for some steps and a Notion or Docs URL for others, and authored() drops
  // the URLs.
  const sopTitle = isWorkflowAuthored(task) ? authored(task.taskSop) : null;
  const description = isWorkflowAuthored(task) ? authored(task.taskDescription) : null;
  const status = pick(STATUS_LABELS, getTaskDisplayStatus(task));
  const priority = pick(PRIORITY_LABELS, task.priority);
  const due = relativeDue(task.due ?? task.taskAssignDueAt, now);
  const blockedReason =
    task?.blockedStatus === BLOCKED_STATUS.Blocked
      ? authored(pick(BLOCKED_REASON_LABELS, task.blockedReason))
      : null;

  const body = [];
  if (description) body.push(ensurePeriod(description));
  body.push(
    escalated && escalationTeam
      ? `This task has been escalated to the ${team} team and is waiting for someone there to pick it up.`
      : claimSentence(ctx.claimState, team),
  );

  // No phase: normalizeDashboardV2Task falls task.phase back to patient.phase,
  // so it is a Patient field in disguise (spec Part B, principle 2).
  const where = [
    pathway ? `on the ${pathway} pathway` : null,
    protocol ? `${protocol} protocol` : null,
  ].filter(Boolean);
  body.push(
    `In Orchestration Manager it sits with the ${team} team${role ? ` as ${role}` : ''}${
      where.length ? `, ${where.join(', ')}` : ''
    }.`,
  );

  const bySop = sopTitle ? ` by ${sopTitle}` : '';
  if (evidence) body.push(`It was flagged as '${evidence}'${bySop}.`);
  else if (stage) body.push(`It is a ${stage} step${bySop}.`);
  else if (sopTitle) body.push(`The SOP for it is ${sopTitle}.`);
  if (type.sentence) body.push(type.sentence);
  if (blockedReason) body.push(`It is currently blocked: ${blockedReason.toLowerCase()}.`);

  const state = [status, priority ? `${priority} priority` : null].filter(Boolean).join(', ');
  if (state) body.push(`It is ${state}${due ? `, due ${due}` : ''}.`);

  return [
    ensurePeriod(lead),
    body.join(' '),
    'What are the steps to complete this task, and who handles it?',
  ].join('\n');
};

/**
 * The deep link, or null when the card must be hidden: no Cortex origin for the
 * org, a completed task, no safe message, or anything Cortex's own screens
 * would block or warn on.
 */
export const buildCortexAskUrl = (task, ctx = {}) => {
  if (!ctx.origin) return null;
  if (!task || isTaskReadinessComplete(task)) return null;

  const message = buildCortexAskMessage(task, ctx);
  if (!message || message.length > CORTEX_Q_MAX) return null;

  const guard = cortexGuard(message);
  if (guard.hard || guard.soft) return null;

  const params = new URLSearchParams({
    v: CORTEX_CONTRACT_VERSION,
    action: 'ask',
    q: message,
    src: CORTEX_SOURCE,
  });
  const ref = `${task.id ?? ''}`.trim();
  if (CORTEX_REF_RE.test(ref)) params.set('ref', ref);

  const url = `${ctx.origin.replace(/\/$/, '')}/?${params.toString()}`;
  return url.length > CORTEX_URL_MAX ? null : url;
};
